import { ChangeDetectionStrategy, Component, ElementRef, computed, input, model, signal, viewChildren } from '@angular/core';
import { ISocialNetwork } from '../../../core/models/catalog.model';

export interface ISocialItem { idSocialNetwork: string; value: string; }

/** Muestra el prefijo sin «https://» como en el diseño (instagram.com/, +52…). */
export function displayPrefix(sn: ISocialNetwork | undefined): string {
  if (!sn) return '';
  if (sn.code === 'whatsapp') return '+52';
  return (sn.prefix || '').replace(/^https?:\/\/(www\.)?/, '') || 'https://';
}

/** Validación por tipo de red: WhatsApp = 10 dígitos; sitio web = dominio; resto = usuario sin espacios. */
export function socialError(item: ISocialItem, sn: ISocialNetwork | undefined): string | null {
  const v = (item.value || '').trim();
  if (!v) return 'Escribe tu usuario o enlace.';
  if (sn?.code === 'whatsapp' && !/^\d{10}$/.test(v.replace(/\D/g, ''))) return 'Escribe un teléfono de 10 dígitos.';
  if (sn?.code === 'web' && !/^[\w-]+(\.[\w-]+)+(\/.*)?$/.test(v.replace(/^https?:\/\//, ''))) return 'Escribe un dominio válido, p. ej. tunegocio.mx';
  if (sn?.code !== 'web' && sn?.code !== 'whatsapp' && /\s/.test(v)) return 'Sin espacios.';
  return null;
}

/** Redes del negocio tomadas del catálogo (Agregar negocio · paso 4 y Ajustes · Redes). */
@Component({
  selector: 'app-social-editor',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (items().length) {
      <section class="group">
        <p class="cr-eyebrow cr-text-tertiary">Agregadas · {{ items().length }}</p>
        @for (it of items(); track it.idSocialNetwork; let i = $index) {
          @let sn = byId(it.idSocialNetwork);
          <div class="cr-field">
            <label class="cr-field__label" [for]="'sn-' + it.idSocialNetwork">{{ sn?.name }}</label>
            <div class="row">
              <div class="input" [class.is-invalid]="showErrors() && errors()[i]">
                <app-social-glyph [code]="sn?.code" [size]="16" [tile]="true" [tileSize]="32" />
                <span class="prefix">{{ prefix(sn) }}</span>
                <input #valueInput [id]="'sn-' + it.idSocialNetwork" [value]="it.value" [placeholder]="sn?.placeholder || ''"
                  [attr.inputmode]="sn?.code === 'whatsapp' ? 'tel' : null" (input)="setValue(i, $any($event.target).value)" />
              </div>
              <button type="button" class="trash" [attr.aria-label]="'Quitar ' + sn?.name" (click)="remove(i)"><cr-icon name="trash" [size]="18" /></button>
            </div>
            @if (showErrors() && errors()[i]; as err) { <p class="cr-field__error">{{ err }}</p> }
          </div>
        }
      </section>
    }
    @if (available().length) {
      <section class="group">
        <p class="cr-eyebrow cr-text-tertiary">Disponibles</p>
        <ul class="list">
          @for (sn of available(); track sn.idSocialNetwork) {
            <li>
              <app-social-glyph [code]="sn.code" [size]="18" [tile]="true" [tileSize]="36" />
              <div class="txt"><p>{{ sn.name }}</p><span>{{ prefix(sn) }}</span></div>
              <button type="button" class="cr-btn cr-btn--secondary cr-btn--s add" (click)="add(sn)">
                <cr-icon name="plus" [size]="16" /><span class="cr-hide-phone">Agregar</span>
              </button>
            </li>
          }
        </ul>
      </section>
    }
    @if (!catalog().length) { <p class="cr-body-s cr-text-tertiary">El catálogo de redes está vacío.</p> }
  `,
  styles: [`
    :host{display:flex;flex-direction:column;gap:40px}
    .group{display:flex;flex-direction:column;gap:12px}
    .group > p{margin:0}
    .row{display:flex;gap:8px;align-items:center}
    .input{flex:1;min-width:0;height:56px;display:flex;align-items:center;gap:10px;padding:0 14px 0 12px;border:1.2px solid var(--cr-border-subtle);border-radius:16px;background:#fff}
    .input:focus-within{border:2px solid var(--cr-brand-teal);box-shadow:0 0 0 4px rgb(10 92 89 / .16)}
    .input.is-invalid{border:2px solid var(--cr-danger)}
    .prefix{font:400 17px/24px var(--cr-font-body);color:var(--cr-text-tertiary);white-space:nowrap}
    .input input{flex:1;min-width:0;border:0;outline:0;font:400 17px/24px var(--cr-font-body);background:transparent;padding:0}
    .trash{width:56px;height:56px;border-radius:16px;border:0;background:var(--cr-bg-surface-2);display:grid;place-items:center;cursor:pointer;color:var(--cr-text-primary);flex:none}
    .list{list-style:none;margin:0;padding:4px 16px;border:1px solid var(--cr-border-subtle);border-radius:20px;background:#fff}
    .list li{display:flex;align-items:center;gap:12px;min-height:67px;border-bottom:1px solid var(--cr-border-subtle)}
    .list li:last-child{border-bottom:0}
    .txt{flex:1;min-width:0}
    .txt p{margin:0;font:600 15px/20px var(--cr-font-body)}
    .txt span{font:400 13px/18px var(--cr-font-body);color:var(--cr-text-secondary)}
    @media (max-width:640px){ .add{width:36px;padding:0} .prefix{font-size:15px} .input input{font-size:15px} }
  `],
})
export class SocialEditorComponent {
  readonly catalog = input<ISocialNetwork[]>([]);
  readonly items = model<ISocialItem[]>([]);
  readonly showErrors = input<boolean>(false);
  private inputs = viewChildren<ElementRef<HTMLInputElement>>('valueInput');
  private focusLast = signal(false);

  readonly available = computed(() => this.catalog().filter(sn => !this.items().some(i => i.idSocialNetwork === sn.idSocialNetwork)));
  readonly errors = computed(() => this.items().map(it => socialError(it, this.byId(it.idSocialNetwork))));

  byId(id: string): ISocialNetwork | undefined {
    return this.catalog().find(s => s.idSocialNetwork === id);
  }

  prefix(sn: ISocialNetwork | undefined): string { return displayPrefix(sn); }

  add(sn: ISocialNetwork): void {
    this.items.set([...this.items(), { idSocialNetwork: sn.idSocialNetwork, value: '' }]);
    setTimeout(() => this.inputs().at(-1)?.nativeElement.focus());
  }

  remove(i: number): void {
    this.items.set(this.items().filter((_, k) => k !== i));
  }

  setValue(i: number, value: string): void {
    this.items.set(this.items().map((it, k) => (k === i ? { ...it, value } : it)));
  }
}
